import type { CSSProperties } from "react";

/**
 * React port of the C# IconTagHelper
 * (src/LoadAndFlow.Web.LoadAndFlow/TagHelpers/IconTagHelper.cs).
 *
 * That helper read the SVG off disk at render time and inlined it, injecting
 * size and accessibility attributes. Inlining matters: the icons are drawn
 * with `stroke="currentColor"`, so they inherit text colour from their
 * container. Referencing them as <img src="/icons/..."> instead would render
 * every icon black and break dark mode.
 *
 * Here Vite inlines them at build time via a raw glob import, which keeps the
 * same behaviour without a runtime fetch per icon.
 */
const modules = import.meta.glob<string>("../assets/icons/**/*.svg", {
  query: "?raw",
  import: "default",
  eager: true,
});

const icons = new Map<string, string>();
for (const [path, svg] of Object.entries(modules)) {
  const match = /icons\/([^/]+)\/([^/]+)\.svg$/.exec(path);
  if (match) icons.set(`${match[1]}/${match[2]}`, svg);
}

/** Matches the size ramp in IconTagHelper.Process. */
const SIZE_CLASSES: Record<string, string> = {
  xs: "w-3 h-3",
  sm: "w-4 h-4",
  md: "w-5 h-5",
  lg: "w-6 h-6",
  xl: "w-8 h-8",
};

export interface IconProps {
  name: string;
  /** Icon category directory: "shared" (default) or "lf". */
  cat?: "shared" | "lf";
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  className?: string;
  /**
   * Set for meaningful icons; omitted icons are treated as decorative and
   * hidden from assistive technology, as in the C# helper.
   */
  "aria-label"?: string;
  style?: CSSProperties;
}

export default function Icon({
  name,
  cat = "shared",
  size = "md",
  className = "",
  "aria-label": ariaLabel,
  style,
}: IconProps) {
  const sizeClass = SIZE_CLASSES[size] ?? SIZE_CLASSES.md;
  const classes = `${sizeClass} shrink-0${className ? ` ${className}` : ""}`;
  const svg = icons.get(`${cat}/${name}`);

  // The C# helper rendered a titled placeholder rather than throwing, so a
  // missing icon degrades to a gap instead of taking the page down.
  if (!svg) {
    return (
      <span
        className={`${sizeClass} inline-block`}
        title={`Icon not found: ${cat}/${name}`}
        data-testid="icon-missing"
      />
    );
  }

  const accessibility = ariaLabel
    ? { role: "img", "aria-label": ariaLabel }
    : { "aria-hidden": true as const };

  return (
    <span
      {...accessibility}
      className="contents"
      style={style}
      // The SVGs are build-time assets from our own repo, not user input.
      dangerouslySetInnerHTML={{ __html: svg.replace("<svg ", `<svg class="${classes}" `) }}
    />
  );
}
